import { makeAutoObservable, runInAction } from 'mobx';
import { ErrorStatusEnum, gatewayConfig } from '@app/common';
import { io, Socket } from 'socket.io-client';
import type { RootStore } from './root-store';
import { PortfolioDialogStore } from './portfolio-dialog-store';
import { routes } from '../../config/routes';
import { PortfolioMobxDto } from '../mobx/dtos/portfolio/portfolio-mobx-dto';
import { PortfolioOverviewMobxDto } from '../mobx/dtos/portfolio/portfolio-overview-mobx-dto';
import { UpdatePortfolioMobxDto } from '../mobx/dtos/portfolio/update-portfolio-mobx-dto';
import { SharePortfolioDialogStore } from './share-portfolio-dialog-store';
import { PortfolioTypeEnum } from '../../defs/api';

export type PortfolioStoreHydration = {
    sharedPortfolio: PortfolioMobxDto;
};

type PortfolioSection = 'active' | 'excluded';

/**
 * Slouží pro:
 * - zobrazení portfolií v dropdownu
 * - zobrazení portfolií na stránce moje portfolia
 * - práci s vybraným portfoliem
 */
export class PortfolioStore {
    rootStore: RootStore;

    portfolioDialogStore: PortfolioDialogStore;

    portfolios: PortfolioMobxDto[] = [];

    pinnedPortfolios: PortfolioMobxDto[] = [];

    loading = false;

    overview: PortfolioOverviewMobxDto | null = null;

    sharedPortfolio: PortfolioMobxDto | null = null;

    sharePortfolioDialogStore: SharePortfolioDialogStore;

    isSharedPortfolioDiscountModalOpened = false;

    portfolioSocket: Socket | null = null;

    isPortfolioListStale = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.portfolioDialogStore = new PortfolioDialogStore(this);
        this.sharePortfolioDialogStore = new SharePortfolioDialogStore(this);

        makeAutoObservable(this, {
            rootStore: false,
            portfolioDialogStore: false,
            sharePortfolioDialogStore: false,
        });
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    invalidatePortfolios() {
        this.isPortfolioListStale = true;
    }

    openSharedPortfolioDiscountModal = () => {
        this.isSharedPortfolioDiscountModalOpened = true;
    };

    closeSharedPortfolioDiscountModal = () => {
        this.isSharedPortfolioDiscountModalOpened = false;
    };

    hydrate(): PortfolioStoreHydration {
        const sharedPortfolio = JSON.parse(JSON.stringify(this.sharedPortfolio));
        return { sharedPortfolio };
    }

    rehydrate(data: PortfolioStoreHydration): void {
        if (data.sharedPortfolio) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.sharedPortfolio = PortfolioMobxDto.create(data.sharedPortfolio as any);
        }
    }

    public connectToPortfolioSocket(portfolioId: string): void {
        if (this.portfolioSocket) {
            const auth = this.portfolioSocket.auth as { portfolio_id: string };
            if (auth.portfolio_id !== portfolioId) {
                this.portfolioSocket.close();
                this.portfolioSocket = null;
            }
        }

        if (!this.portfolioSocket) {
            const jwtToken = this.rootStore.cookieService.getJwtToken();
            if (jwtToken) {
                this.portfolioSocket = io(
                    `${process.env.NEXT_PUBLIC_SOCKET_URL}/${gatewayConfig().channels.portfolio}`,
                    {
                        auth: {
                            authorization: jwtToken,
                            portfolio_id: portfolioId,
                        },
                        reconnection: true,
                        secure: true,
                        reconnectionAttempts: Infinity,
                        reconnectionDelay: 1000,
                        transports: ['websocket'],
                    },
                );
            } else {
                // eslint-disable-next-line
                console.error('portfolio socket connection failed no JWT token');
            }
        }
    }

    async fetchSharedPortfolio(shareSlug: string): Promise<void> {
        this.startLoading();
        try {
            const portfolio = await this.rootStore.apiClient.portfolioController.sharedPortfolioControllerShared({
                shareSlug,
            });

            if (portfolio) {
                runInAction(() => {
                    this.sharedPortfolio = PortfolioMobxDto.create(portfolio);
                });
            }
        } catch (e) {
            this.stopLoading();
            throw e;
        } finally {
            this.stopLoading();
        }
    }

    async fetchPortfolioList(): Promise<boolean> {
        try {
            this.startLoading();
            const fetchedPortfolios = await this.rootStore.apiClient.portfolioController.portfolioControllerGetList();
            runInAction(() => {
                if (fetchedPortfolios !== null) {
                    this.portfolios = PortfolioMobxDto.createFromArray(fetchedPortfolios);
                }
                this.isPortfolioListStale = false;
                this.stopLoading();
            });
            return true;
        } catch (e) {
            window.location.href = `${routes.web.index}?status=${ErrorStatusEnum.PortfoliosLoadFailed}`;
            return false;
        }
    }

    async fetchPortfolioOverview(): Promise<void> {
        try {
            this.startLoading();
            const fetchedOverview = await this.rootStore.apiClient.portfolioController.portfolioControllerGetOverview();
            if (fetchedOverview) {
                runInAction(() => {
                    this.overview = PortfolioOverviewMobxDto.create(fetchedOverview);
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    async selectPortfolio(portfolioId: string | null, noRedirect?: boolean): Promise<void> {
        if (!portfolioId) {
            return;
        }

        this.rootStore.tradeStore.tradeImportDialogStore.resetMessages();
        try {
            this.startLoading();
            await this.rootStore.apiClient.portfolioController.portfolioControllerSetSelected({ id: portfolioId });

            if (!noRedirect) {
                const url = new URL(window.location.href);
                url.searchParams.delete('lastImported');
                window.location.replace(url.href);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }

        // TODO: root-store-provider issue
        // portfolio.setSelected(true);
        // this.portfolios.forEach((p) => {
        //     if (p.id !== portfolio.id) {
        //         p.setSelected(false);
        //     }
        // });

        // await this.rootStore.statisticStore.fetchIndexData();
        // await Router.replace(Router.asPath);
    }

    get selectedPortfolio(): PortfolioMobxDto {
        if (this.sharedPortfolio?.id) {
            return this.sharedPortfolio;
        }

        const portfolio = this.portfolios.find((p) => p.selected);
        if (portfolio) {
            return portfolio;
        }
        if (!portfolio && this.portfolios.length > 0) {
            return this.portfolios[0];
        }
        throw new Error('No available portfolios');
    }

    // get hasToUploadTrades(): boolean {
    //     const hasPortfolioValue =
    //         this.selectedPortfolio.invested > 0 ||
    //         this.selectedPortfolio.realizedInvested > 0 ||
    //         this.selectedPortfolio.portfolioValue > 0 ||
    //         this.selectedPortfolio.realizedPortfolioValue > 0;
    //
    //     return !hasPortfolioValue || this.selectedPortfolio.isRecalculating;
    // }

    get hasPortfolio(): boolean {
        return this.portfolios.length > 0;
    }

    waitForRecalculatingDone(portfolioId: string, message: string) {
        this.connectToPortfolioSocket(portfolioId);
        const currentPtf = this.portfolios.find((p) => p.id === portfolioId);

        if (currentPtf?.isRecalculating) {
            this.rootStore.alertStore.setWarningMessage(message);
            this.rootStore.alertStore.setLoading(true);
            this.rootStore.alertStore.resetPortfolioRecalculationProgress();
        }

        const onProgress = () => {
            currentPtf?.setIsRecalculating(true);

            this.rootStore.alertStore.setWarningMessage(message);
            this.rootStore.alertStore.setLoading(true);
        };
        const onCompleted = () => {
            currentPtf?.setIsRecalculating(false);

            window.location.reload();

            this.rootStore.alertStore.setWarningMessage('');
            this.rootStore.alertStore.setLoading(false);
            this.rootStore.alertStore.resetPortfolioRecalculationProgress();
        };

        this.rootStore.alertStore.setPortfolioRecalculationProgress(portfolioId, onProgress, onCompleted);
    }

    clearSharedPortfolio() {
        this.sharedPortfolio = null;
    }

    async reorderPortfolios(
        sectionOrSourceIndex: PortfolioSection | number,
        sourceOrDestinationIndex: number,
        destinationIndex?: number,
        destinationSection?: PortfolioSection,
    ) {
        if (typeof sectionOrSourceIndex === 'number') {
            const sourceIndex = sectionOrSourceIndex;
            const oldDestinationIndex = sourceOrDestinationIndex;

            if (sourceIndex === oldDestinationIndex) {
                return;
            }

            const reorderedPortfolios = Array.from(this.portfolios);
            const [movedItem] = reorderedPortfolios.splice(sourceIndex, 1);
            reorderedPortfolios.splice(oldDestinationIndex, 0, movedItem);
            this.portfolios = reorderedPortfolios;

            try {
                await this.rootStore.apiClient.portfolioController.portfolioControllerReorderPortfolios({
                    reorderPortfoliosDto: {
                        portfolioIds: reorderedPortfolios.map((p) => p.id),
                    },
                });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            }
            return;
        }

        const section = sectionOrSourceIndex;
        const sourceIndex = sourceOrDestinationIndex;

        if (destinationIndex === undefined) {
            return;
        }

        const targetSection = destinationSection ?? section;

        if (section === targetSection && sourceIndex === destinationIndex) {
            return;
        }

        const summary = this.portfolios.find((p) => p.type === PortfolioTypeEnum.Summary);
        const activePortfolios = this.portfolios.filter(
            (p) => p.type !== PortfolioTypeEnum.Summary && p.includedInSummary,
        );
        const excludedPortfolios = this.portfolios.filter(
            (p) => p.type !== PortfolioTypeEnum.Summary && !p.includedInSummary,
        );
        const sourcePortfolios = section === 'active' ? activePortfolios : excludedPortfolios;
        const targetPortfolios = targetSection === 'active' ? activePortfolios : excludedPortfolios;

        if (sourceIndex >= sourcePortfolios.length || destinationIndex > targetPortfolios.length) {
            return;
        }

        if (section === 'active' && targetSection === 'excluded' && activePortfolios.length === 1) {
            return;
        }

        const [movedItem] = sourcePortfolios.splice(sourceIndex, 1);
        movedItem.includedInSummary = targetSection === 'active';
        targetPortfolios.splice(destinationIndex, 0, movedItem);

        const reorderedPortfolios = [...activePortfolios, ...excludedPortfolios, ...(summary ? [summary] : [])];
        this.portfolios = reorderedPortfolios;

        try {
            if (section !== targetSection) {
                const updateData = UpdatePortfolioMobxDto.createFromPortfolio(movedItem);
                await this.rootStore.apiClient.portfolioController.portfolioControllerUpdate({
                    id: updateData.id,
                    updatePortfolioDto: updateData.toApiData(),
                });
            }

            await this.rootStore.apiClient.portfolioController.portfolioControllerReorderPortfolios({
                reorderPortfoliosDto: {
                    portfolioIds: reorderedPortfolios.map((p) => p.id),
                },
            });

            if (section !== targetSection) {
                await this.fetchPortfolioList();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            await this.fetchPortfolioList();
        }
    }

    async fetchPinnedPortfolios(): Promise<void> {
        this.startLoading();
        try {
            const pinnedPortfolios = await this.rootStore.apiClient.portfolioController.portfolioControllerGetPinned();

            if (pinnedPortfolios) {
                this.pinnedPortfolios = PortfolioMobxDto.createFromArray(pinnedPortfolios);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    async changeCurrency(currencyCode: string): Promise<void> {
        try {
            this.startLoading();

            const updateData = UpdatePortfolioMobxDto.createFromPortfolio(this.selectedPortfolio);
            updateData.currencyCode = currencyCode;

            await this.rootStore.apiClient.portfolioController.portfolioControllerUpdate({
                id: updateData.id,
                updatePortfolioDto: updateData.toApiData(),
            });

            await this.fetchPortfolioList();
            this.rootStore.alertStore.setSuccessMessage('Currency changed successfully');
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }
}
